/**
 * TAPStore recording-URL composition.
 *
 * AD-2: the template lives here and nowhere else — CLT copies it into four
 * sites. The deferred server-side URL issuer deletes this whole file.
 *
 * @ticket EB-4196
 */

/** AD-1, confirmed against a real recording 2026-08-28. */
const RECORDING_PATH_TEMPLATE = '{base}/YYYY/MM/DD/{cchsid}_{agent}.wav';

/**
 * Berlin, not UTC: the store runs in Berlin, and a UTC folder would misfile
 * every call between 00:00 and 02:00 local. Safe from any browser timezone —
 * the backend serialises with an explicit offset.
 */
const TAPSTORE_DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function toBerlinDateParts(
  iso: string | null | undefined,
): { year: string; month: string; day: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = TAPSTORE_DATE_FMT.formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  if (!year || !month || !day) return null;
  return { year, month, day };
}

/**
 * AD-3: coerce at the boundary instead of retyping the DTO — `agent` is
 * declared `string | null` but PS-API sends an `Integer`, and pinning either
 * one breaks on a serializer change. `null` keeps `NaN` out of the URL, where
 * it would 404 silently.
 */
export function toAgentId(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  // Only strings are coerced. A bare `Number(raw)` would turn `true` into 1,
  // `[]` into 0 and `['5']` into 5 — silently inventing an agent id.
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * Compose the TAPStore address for one recording.
 *
 * `callStartTimestamp` must be the call's Started time (AD-12) — an
 * `accountingdata` row's time is that *event's*, so a call spanning midnight
 * would land in a folder decided by response ordering.
 *
 * AD-11: one path only, no ±1-day walk. A recording filed under the call's end
 * date shows the empty state — accepted (CLT behaves identically); the deferred
 * endpoint closes it for all callers.
 *
 * @returns null when the date, cchsid or agent is unusable.
 */
export function buildRecordingUrl(
  baseUrl: string,
  cchsid: string,
  callStartTimestamp: string | null | undefined,
  agentId: number,
): string | null {
  const trimmedCchsid = String(cchsid ?? '').trim();
  if (!trimmedCchsid) return null;
  if (!Number.isFinite(agentId)) return null;
  const date = toBerlinDateParts(callStartTimestamp);
  if (!date) return null;
  // CLT's params.php carries a trailing slash; environment.tapStoreUrl does not.
  const base = String(baseUrl ?? '').replace(/\/+$/, '');
  return RECORDING_PATH_TEMPLATE.replace('{base}', base)
    .replace('YYYY', date.year)
    .replace('MM', date.month)
    .replace('DD', date.day)
    .replace('{cchsid}', encodeURIComponent(trimmedCchsid))
    .replace('{agent}', String(agentId));
}
